import { SEO } from "@/components/common/seo";

const PolicySection = ({
  id,
  title,
  children,
}: {
  id?: string;
  title: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="mb-10 scroll-mt-24">
    <h2 className="font-display text-xl font-semibold text-foreground mb-4 border-b border-border pb-2">
      {title}
    </h2>
    <div className="text-foreground/85 text-[16px] leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const TermsAndConditions = () => {
  return (
    <>
      <SEO canonical="/terms-and-conditions" />
      <div className="min-h-screen bg-background" id="top">
        <div className="w-full max-w-7xl xl:max-w-[80rem] 2xl:max-w-[88rem] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-10 sm:py-12 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_300px] 2xl:grid-cols-[1fr_320px] gap-8 md:gap-10 lg:gap-12 xl:gap-16">
            <article className="min-w-0 order-2 lg:order-1">
              <header className="mb-10 flex flex-col items-center justify-center gap-1">
                <h1 className="font-display text-3xl md:text-5xl font-bold text-foreground tracking-tight">
                  Terms and Conditions
                </h1>
                <p className="text-muted-foreground text-sm mt-2">
                  Last updated: February 11, 2026
                </p>
              </header>

              <PolicySection id="interpretation-and-definitions" title="Interpretation and Definitions">
                <h3 className="font-display font-semibold text-foreground mt-4 mb-2">Interpretation</h3>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  Terms with capitalized initial letters are defined under the following conditions and hold the same meaning regardless of whether they appear in singular or plural form.
                </p>
                <h3 className="font-display font-semibold text-foreground mt-4 mb-2">Definitions</h3>
                <dl className="mt-2 space-y-2">
                  <div>
                    <dt className="font-medium text-foreground">Company</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      Refers to Somatiq AI Labs Private Limited, No. 07/3, Second Floor, 18th Main Rd, Jayanagara 9th Block, Bengaluru, Karnataka 560041.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">Service</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      Refers to the Website and the AI-driven architectural visualization and design tools hosted therein.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">Terms</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      Means these Terms and Conditions that form the entire agreement between You and the Company regarding the use of the Service.
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-foreground">You</dt>
                    <dd className="ml-4 text-foreground/80 text-[16px] leading-relaxed">
                      The individual, company, or legal entity accessing or using the Service.
                    </dd>
                  </div>
                </dl>
              </PolicySection>

              <PolicySection id="acknowledgment" title="Acknowledgment">
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  Your access to the Service is conditioned on Your acceptance of these Terms. These apply to all visitors, users, and clients.
                </p>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  By using the Service, You represent that You are at least 18 years of age. Somatiq AI Labs does not permit the use of our professional design tools by individuals under 18.
                </p>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  Your use is also subject to our Privacy Policy, which details our data practices and your privacy rights. Please review it carefully.
                </p>
              </PolicySection>

              <PolicySection id="intellectual-property-use-of-ai-output" title="Intellectual Property & Use of AI Output">
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  All original content, features, and functionality on the Website are the exclusive property of Somatiq AI Labs and its licensors.
                </p>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  Usage of AI-generated architectural renderings is subject to specific project agreements. Unauthorized redistribution or commercial resale of the Service's core AI models is strictly prohibited.
                </p>
              </PolicySection>

              <PolicySection id="limitation-of-liability" title="Limitation of Liability">
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  To the maximum extent permitted by law, the Company's total liability shall be limited to the amount actually paid by You through the Service, or 100 USD if no purchase has been made.
                </p>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  In no event shall the Company be liable for consequential, incidental, or indirect damages, including loss of profits or data, arising from the use or inability to use our AI design services.
                </p>
              </PolicySection>

              <PolicySection id="as-is-and-as-available-disclaimer" title='"AS IS" and "AS AVAILABLE" Disclaimer'>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  The Service is provided to You &quot;AS IS.&quot; We disclaim all warranties, including merchantability and fitness for a particular architectural purpose.
                </p>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  We do not warrant that the AI outputs will be error-free, meet all building code requirements without professional verification, or that the Service will be uninterrupted.
                </p>
              </PolicySection>

              <PolicySection id="governing-law-dispute-resolution" title="Governing Law & Dispute Resolution">
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  The laws of Karnataka, India, shall govern these Terms. Any disputes shall first be attempted to be resolved through informal negotiation.
                </p>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  If informal resolution fails, the courts of Bengaluru shall have exclusive jurisdiction over any legal proceedings.
                </p>
              </PolicySection>

              <PolicySection id="changes-to-these-terms" title="Changes to These Terms">
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  We reserve the right to modify these Terms at any time. For material changes, we will provide at least 30 days' notice.
                </p>
                <p className="text-foreground/85 text-[16px] leading-relaxed">
                  Continued use of the Service after changes are posted constitutes your acceptance of the new Terms.
                </p>
              </PolicySection>

              <PolicySection id="contact-us" title="Contact Us">
                <p className="mb-3">For inquiries regarding these Terms, please contact us:</p>
                <ul className="list-none space-y-2">
                  <li>
                    <span className="font-medium text-foreground">Email: </span>
                    <a href="mailto:contact@somatiq.ai" className="text-(--brilliant-azure) hover:underline">
                      contact@somatiq.ai
                    </a>
                  </li>
                  <li>
                    <span className="font-medium text-foreground">Direct Support: </span>
                    <a href="https://www.somatiq.ai" target="_blank" rel="noopener noreferrer" className="text-(--brilliant-azure) hover:underline">
                      https://www.somatiq.ai
                    </a>
                  </li>
                </ul>
              </PolicySection>
            </article>

            <aside className="lg:sticky lg:top-24 lg:self-start order-1 lg:order-2">
              <nav className="px-6 py-5 rounded-lg border border-border bg-background " aria-label="Table of contents">
                <h2 className="font-display font-bold text-foreground text-base mb-4">
                  Table of contents
                </h2>
                <ol className="space-y-3 text-[15px] list-none">
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">1.</span>
                    <a href="#interpretation-and-definitions" className="toc-link">Interpretation and Definitions</a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">2.</span>
                    <a href="#acknowledgment" className="toc-link">Acknowledgment</a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">3.</span>
                    <a href="#intellectual-property-use-of-ai-output" className="toc-link">Intellectual Property & Use of AI Output</a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">4.</span>
                    <a href="#limitation-of-liability" className="toc-link">Limitation of Liability</a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">5.</span>
                    <a href="#as-is-and-as-available-disclaimer" className="toc-link">&quot;AS IS&quot; and &quot;AS AVAILABLE&quot; Disclaimer</a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">6.</span>
                    <a href="#governing-law-dispute-resolution" className="toc-link">Governing Law & Dispute Resolution</a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">7.</span>
                    <a href="#changes-to-these-terms" className="toc-link">Changes to These Terms</a>
                  </li>
                  <li className="flex gap-2">
                    <span className="shrink-0 text-foreground/70">8.</span>
                    <a href="#contact-us" className="toc-link">Contact Us</a>
                  </li>
                </ol>
                <p className="mt-5 pt-4 border-t border-border">
                  <a href="#top" className="toc-link font-semibold inline-flex items-center gap-1">
                    Back to top ↑
                  </a>
                </p>
              </nav>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default TermsAndConditions;
